// Gera o título de cobrança (boleto/PIX) real da taxa de um pedido, via API de
// Títulos local (`titulosApi`). Reaproveita `protocoloService.extrairIdsUsuario`
// (mesma lógica de token/fallback já usada em toda a integração de Protocolo).
import titulosApi from '../../../../services/titulosApi';
import { protocoloService } from '../../../../services';
import logger from '../../../../utils/logger';
import { resolverPagador } from './pagadorResolverService';

/**
 * tipoLancamentoId pode ser um número fixo ou, pros pedidos de valor variável
 * cujo lançamento também muda por opção (Distrato de Sociedade, Alteração de
 * Nome, 2ª Via de Documentos), um condicional { field, map }.
 */
const resolveTipoLancamentoId = (tipoLancamentoId, formData = {}) => {
  if (typeof tipoLancamentoId === 'number') {
    return tipoLancamentoId;
  }

  const valor = formData?.[tipoLancamentoId?.field];
  const resolvido = tipoLancamentoId?.map?.[valor];
  if (!resolvido) {
    throw new Error(`Não foi possível resolver tipoLancamentoId (campo "${tipoLancamentoId?.field}", valor "${valor}")`);
  }
  return resolvido;
};

/**
 * @param {Object} params
 * @param {Object} params.config - config.js do pedido (usa config.tituloConfig)
 * @param {Object} params.formData - dados do formulário já preenchidos
 * @param {Object} params.user - usuário logado
 * @param {number} params.valor - valor já calculado da taxa (calculatePaymentAmount)
 * @param {number} params.requerimentoId - vincula o título ao requerimento; com ele
 *   a API é idempotente (nova chamada devolve a mesma cobrança, não cria outra)
 * @returns {Promise<{linhaDigitavel: string, pixCopiaECola: string, urlBoleto: string}>}
 */
export async function gerarTitulo({ config, formData, user, valor, requerimentoId }) {
  if (!config.tituloConfig) {
    throw new Error('Pedido sem tituloConfig configurado');
  }

  const { enderecoOrigem } = config.tituloConfig;
  const tipoLancamentoId = resolveTipoLancamentoId(config.tituloConfig.tipoLancamentoId, formData);

  const { pessoaId, usuarioId } = protocoloService.extrairIdsUsuario(user);
  const pagador = resolverPagador(user, enderecoOrigem, formData);

  try {
    const response = await titulosApi.post('/titulos', {
      pessoaId: Number(pessoaId),
      tipoLancamentoId,
      valor,
      createdBy: Number(usuarioId),
      requerimentoId: requerimentoId ? Number(requerimentoId) : null,
      pagador
    });

    return response.data?.data || response.data;
  } catch (error) {
    logger.error('Erro ao gerar título:', error);
    // 409: outra emissão do mesmo requerimento em andamento (ex: duplo clique),
    // ou título existente vencido/em situação que não aceita nova cobrança.
    if (error.response?.status === 409) {
      throw new Error(error.response.data?.message || 'Já existe uma cobrança sendo gerada para este pedido. Aguarde alguns segundos e tente novamente.');
    }
    throw error.response?.data || error;
  }
}

/**
 * Cobrança já emitida de um requerimento (GET /titulos/requerimento/:id) — usada
 * em Minhas Solicitações pra pessoa rever o PIX/boleto depois de sair do pedido.
 * Devolve null quando o requerimento não tem título (404), o que acontece com
 * todo pedido gerado antes de o título passar a ser vinculado ao requerimento.
 * @returns {Promise<{tituloId, situacao, valor, dataVencimento, linhaDigitavel, pixCopiaECola}|null>}
 */
export async function buscarCobrancaPorRequerimento({ requerimentoId, user }) {
  const { pessoaId } = protocoloService.extrairIdsUsuario(user);

  try {
    const response = await titulosApi.get(`/titulos/requerimento/${requerimentoId}`, {
      params: { pessoaId: Number(pessoaId) }
    });
    return response.data?.data || null;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    logger.error('Erro ao buscar cobrança do requerimento:', error);
    throw error.response?.data || error;
  }
}

// Situação do título (ContaReceber.Situacao) em que ainda cabe pagar.
const SITUACAO_TITULO_A_RECEBER = 5;

/**
 * Só faz sentido mostrar PIX/linha digitável de título "A receber". Sem
 * situação na resposta, considera em aberto (não esconde o código à toa).
 */
export const isCobrancaEmAberto = (situacao) =>
  situacao === undefined || situacao === null || Number(situacao) === SITUACAO_TITULO_A_RECEBER;

/**
 * Vencida quando a data de vencimento (YYYY-MM-DD) é anterior a hoje — no próprio
 * dia do vencimento ainda dá pra pagar. Compara as strings, sem passar por Date,
 * pra não cair no dia anterior por causa do fuso.
 */
export const isCobrancaVencida = (dataVencimento, hoje = new Date()) => {
  if (!dataVencimento) return false;
  const pad = (n) => String(n).padStart(2, '0');
  const hojeIso = `${hoje.getFullYear()}-${pad(hoje.getMonth() + 1)}-${pad(hoje.getDate())}`;
  return String(dataVencimento).slice(0, 10) < hojeIso;
};
